import { useRef, useState } from "react";
import type { MouseEvent } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { CSSTransition } from "react-transition-group";

import moment from "moment";

import { getCustomerSettings } from "@/api/settings";

import {
    BulletPointIcon,
    CalendarIcon24,
    CardArrowIcon64,
    ClockIcon24,
    PlusIcon24,
    TimeIcon24,
    UsersIcon24,
} from "@/components/icons";

import type { UserInfo } from "@/types/sessions";

import { Client } from "@/enums/clients";

import { getTimeDifference } from "@/utils/get-time-difference";

import type { SessionItemProps } from "./session-item.types";

import {
    CardArrowContainer,
    SessionIconContainer,
    SessionInfoContainer,
    SessionItemContainer,
    SessionTitle,
    DurationDiv,
    DateDiv,
    TimeDiv,
    StyledImg,
    DetailItemTextSpan,
    UserLabel,
    CrewsAndIconWrapper,
    UnitsWrapper,
    CrewsWrapper,
    IndividualCrewWrapper,
    RemainingCrewsWrapper,
    ViewMoreWrapper,
    DateTimeAndDurationWrapper,
    DateAndTimeWrapper,
    GlobalWrapper,
} from "./session-item.styles";

const SessionItem = ({ session, index }: SessionItemProps) => {
    const navigate = useNavigate();

    const transitionRef = useRef(null);

    const [isViewMoreClicked, setIsViewMoreClicked] = useState<boolean>(false);

    const duration = (from: any, to: any) => {
        return getTimeDifference(new Date(from), new Date(to));
    };

    const { id, name, simulationName, logoUrl, userId } = session;
    const { from, to, scenarioInstanceId, simulationId, scenarioName } = session.sessions[0];

    let sanitizedSimulationName = simulationName.replace(/_/g, " ") ?? "";
    let sanitizedScenarioName = scenarioName;

    // Use '|' in dashboard names to prevent simulation name concatenation
    if (sanitizedSimulationName.startsWith("|")) {
        sanitizedSimulationName = sanitizedSimulationName.substring(1);
        sanitizedScenarioName = "";
    }

    const cardLink = `/sessions/session-analysis?traineeName=${name}&simulationName=${sanitizedSimulationName}&userId=${userId}&from=${from}&to=${to}&scenarioId=${simulationId}&simulationInstanceId=${id}&scenarioInstanceId=${scenarioInstanceId}&name=${sanitizedScenarioName}&logoUrl=${logoUrl}`;
    const handleClick = (event: any) => {
        event.preventDefault();
        navigate(cardLink);

        if (session.users.length !== 0) {
            localStorage.setItem("users", JSON.stringify(session.users));
        }
    };

    const sessionDate = moment(session.sessions[0].from).format("DD/MM/YYYY");
    const fromTime = moment(session.sessions[0].from).format("HH:mm");

    const options = {
        suspense: false,
        refetchOnMount: false,
        useErrorBoundary: true,
    };

    const { data: d } = useQuery([getCustomerSettings], getCustomerSettings, options);

    if (d?.customerName == Client.MLRS) {
        session.simulationName = "TCT - MLRS";
        session.sessions[0].scenarioName = "Detachment Commanders Course";
    }

    const groupNames: Record<string, string> = {};
    let count = 1;

    const groupedCrews = session.users.reduce((acc: Record<string, UserInfo[]>, user: UserInfo) => {
        if (!groupNames[user.grouping]) {
            groupNames[user.grouping] = `crew${count++}`;
        }

        const crewName = groupNames[user.grouping];

        acc[crewName] = [...(acc[crewName] || []), user];

        return acc;
    }, {});

    const firstCrew = Object.values(groupedCrews)[0];
    const remainingCrews = Object.values(groupedCrews).slice(1);

    //users empty array, but still has session info
    const showSessionInfoOnly =
        session.users.length === 0 &&
        sessionDate &&
        fromTime &&
        duration(session.sessions[0].from, session.sessions[0].to)
            ? true
            : false;

    //1 user
    const isSingleUser = session.users.length === 1;

    //3 users
    const isCrew = session.users.length > 0 && session.users.length <= 3;

    //more than 3 users
    const isUnit = session.users.length > 3;

    const handleViewMoreClick = (event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        event.preventDefault(); //prevents the anchor's default navigation

        setIsViewMoreClicked((prevState) => !prevState);
    };

    return (
        <SessionItemContainer
            onClick={(event) => handleClick(event)}
            href={cardLink}
            data-test-id={`session-${index + 1}`}
        >
            <SessionIconContainer>
                {session.logoUrl && <StyledImg src={session.logoUrl} alt="Session Logo" />}
            </SessionIconContainer>

            <SessionInfoContainer>
                {!["interposer-scenario", "", "legacy"].includes(sanitizedScenarioName) ? (
                    <SessionTitle>
                        <span>{sanitizedSimulationName}</span>
                        <BulletPointIcon />
                        <span>{sanitizedScenarioName}</span>
                    </SessionTitle>
                ) : (
                    <SessionTitle>
                        <span>{sanitizedSimulationName}</span>
                    </SessionTitle>
                )}

                <GlobalWrapper>
                    {showSessionInfoOnly && (
                        <DateTimeAndDurationWrapper>
                            <DateAndTimeWrapper>
                                <DateDiv>
                                    <CalendarIcon24 fill="#999999" />
                                    {sessionDate}
                                </DateDiv>

                                <TimeDiv>
                                    <TimeIcon24 fill="#999999" />
                                    {fromTime}
                                </TimeDiv>
                            </DateAndTimeWrapper>

                            <DurationDiv>
                                <BulletPointIcon fill="#999999" />

                                <DetailItemTextSpan>
                                    {duration(session.sessions[0].from, session.sessions[0].to)}
                                </DetailItemTextSpan>
                            </DurationDiv>
                        </DateTimeAndDurationWrapper>
                    )}

                    {(isSingleUser || isCrew) && (
                        <>
                            <IndividualCrewWrapper>
                                {session.users.map((user: UserInfo, index: number) => {
                                    return (
                                        <UserLabel key={`${user}-${index}`}>
                                            <UsersIcon24 fill="#999999" />
                                            {user.name}
                                        </UserLabel>
                                    );
                                })}
                            </IndividualCrewWrapper>

                            <DateTimeAndDurationWrapper>
                                <DateAndTimeWrapper>
                                    <DateDiv>
                                        <CalendarIcon24 fill="#999999" />
                                        {sessionDate}
                                    </DateDiv>

                                    <TimeDiv>
                                        <TimeIcon24 fill="#999999" />
                                        {fromTime}
                                    </TimeDiv>
                                </DateAndTimeWrapper>

                                <DurationDiv>
                                    <BulletPointIcon fill="#999999" />

                                    <DetailItemTextSpan>
                                        {duration(session.sessions[0].from, session.sessions[0].to)}
                                    </DetailItemTextSpan>
                                </DurationDiv>
                            </DateTimeAndDurationWrapper>
                        </>
                    )}

                    {isUnit && (
                        <>
                            <CrewsAndIconWrapper>
                                <UnitsWrapper>
                                    <CrewsWrapper>
                                        <IndividualCrewWrapper>
                                            {firstCrew.map((user: UserInfo, index: number) => {
                                                return (
                                                    <UserLabel key={`${user}-${index}`}>
                                                        <UsersIcon24 fill="#999999" />
                                                        {user.name}
                                                    </UserLabel>
                                                );
                                            })}
                                        </IndividualCrewWrapper>

                                        <CSSTransition
                                            in={isViewMoreClicked}
                                            timeout={200}
                                            classNames="fade-slide"
                                            unmountOnExit
                                            nodeRef={transitionRef}
                                        >
                                            <RemainingCrewsWrapper ref={transitionRef}>
                                                {remainingCrews.map(
                                                    (remainingCrew: UserInfo[], index: number) => {
                                                        return (
                                                            <IndividualCrewWrapper
                                                                key={`remaining-crew-${index}`}
                                                            >
                                                                {remainingCrew.map(
                                                                    (
                                                                        user: UserInfo,
                                                                        index: number,
                                                                    ) => {
                                                                        return (
                                                                            <UserLabel
                                                                                key={`${user}-${index}`}
                                                                            >
                                                                                <UsersIcon24 fill="#999999" />
                                                                                {user.name}
                                                                            </UserLabel>
                                                                        );
                                                                    },
                                                                )}
                                                            </IndividualCrewWrapper>
                                                        );
                                                    },
                                                )}
                                            </RemainingCrewsWrapper>
                                        </CSSTransition>
                                    </CrewsWrapper>
                                </UnitsWrapper>

                                <ViewMoreWrapper onClick={handleViewMoreClick}>
                                    <PlusIcon24 />
                                    {isViewMoreClicked ? "View Less" : "View More"}
                                </ViewMoreWrapper>
                            </CrewsAndIconWrapper>

                            <DateTimeAndDurationWrapper>
                                <DateAndTimeWrapper>
                                    <DateDiv>
                                        <CalendarIcon24 />
                                        {sessionDate}
                                    </DateDiv>

                                    <TimeDiv>
                                        <ClockIcon24 />
                                        {fromTime}
                                    </TimeDiv>
                                </DateAndTimeWrapper>

                                <BulletPointIcon fill="#999999" />

                                <DurationDiv>
                                    {duration(session.sessions[0].from, session.sessions[0].to)}
                                </DurationDiv>
                            </DateTimeAndDurationWrapper>
                        </>
                    )}
                </GlobalWrapper>
            </SessionInfoContainer>

            <CardArrowContainer className="chevron-container">
                <CardArrowIcon64 />
            </CardArrowContainer>
        </SessionItemContainer>
    );
};

export default SessionItem;
